import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Tooltip } from './tooltip.component';

/* TruncatedTooltip
   ----------------
   Wraps an inline form control / element and shows the existing
   <Tooltip> ONLY when that element is actually clipped — using either:
     1) the standard `scrollWidth > clientWidth` check (works for text
        <input> and <select>), or
     2) a canvas-based text width measurement (the only reliable signal
        for <input type="date">, whose native ::-webkit-datetime-edit
        pseudo-elements always report scrollWidth === clientWidth even
        when visibly truncated).

   The wrapper itself is layout-neutral (display: block, full width,
   min-width: 0). The cell that contains it is expected to be
   `position: relative` if a "…" overlay pseudo-element is used. */
interface TruncatedTooltipProps {
  /** Full text to show inside the tooltip when the child is clipped. */
  content: string;
  /** The element being measured for truncation (input / select / span). */
  children: React.ReactNode;
  /** Tooltip position relative to the child. */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Optional class on the measuring wrapper. */
  className?: string;
}

// One shared off-DOM canvas — reused for every measurement so we don't
// allocate a new context on each resize tick.
let measureCanvas: HTMLCanvasElement | null = null;
const getMeasureCtx = (): CanvasRenderingContext2D | null => {
  if (typeof document === 'undefined') return null;
  if (!measureCanvas) measureCanvas = document.createElement('canvas');
  return measureCanvas.getContext('2d');
};

const detectOverflow = (target: HTMLElement, content: string): boolean => {
  // Path A — fast, works for plain text inputs / selects whose
  // internal layout reports a real scrollWidth when content overflows.
  if (target.scrollWidth > target.clientWidth + 1) return true;

  // Path B — text-width fallback for elements whose internal pseudos
  // hide the real overflow (e.g. <input type="date">). Compares the
  // measured text width against the element's effective text-area
  // (clientWidth minus horizontal padding).
  if (!content) return false;
  const ctx = getMeasureCtx();
  if (!ctx) return false;
  const cs = window.getComputedStyle(target);
  // Build a canvas font shorthand from the computed style.
  ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
  const textWidth = ctx.measureText(content).width;
  const padLeft = parseFloat(cs.paddingLeft) || 0;
  const padRight = parseFloat(cs.paddingRight) || 0;
  const available = target.clientWidth - padLeft - padRight;
  // +2px tolerance for sub-pixel rounding so we don't flicker on/off.
  return textWidth > available + 2;
};

export const TruncatedTooltip: React.FC<TruncatedTooltipProps> = ({
  content,
  children,
  position = 'top',
  className,
}) => {
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const [overflows, setOverflows] = useState(false);

  const measure = () => {
    const root = wrapperRef.current;
    if (!root) return;
    const target = (root.firstElementChild as HTMLElement) || root;
    setOverflows(detectOverflow(target, content));
  };

  useLayoutEffect(() => {
    measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  useEffect(() => {
    const root = wrapperRef.current;
    if (!root) return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(root);
    const target = root.firstElementChild as HTMLElement | null;
    if (target) ro.observe(target);
    // Also observe the parent cell — the date input's own width can
    // change without firing the input's resize observer when the
    // grid template recalculates on viewport changes.
    const cell = root.parentElement;
    if (cell) ro.observe(cell);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // `data-overflowing` is consumed by CSS to overlay a "…" indicator
  // on top of children that cannot natively render text-overflow.
  // No inline `position` — that's set on the parent cell so the
  // overlay positions against the cell, not the wrapper.
  const wrapper = (
    <span
      ref={wrapperRef}
      className={`truncated-tooltip-wrapper${className ? ' ' + className : ''}`}
      data-overflowing={overflows ? 'true' : undefined}
      style={{ display: 'block', width: '100%', minWidth: 0 }}
    >
      {children}
    </span>
  );

  if (!overflows) return wrapper;

  return (
    <Tooltip content={content} position={position}>
      {wrapper}
    </Tooltip>
  );
};
